import React, { useEffect, useRef, useState } from 'react';
import I18n from '@/modules/i18n';
import Icon from '@/components/ui/Icon';
import { IAssignmentTypeFilterOption } from '../../types';
import { getSelectedLeafOptions, toggleOption } from './selectionUtils';
import AssignmentTypeChip from './AssignmentTypeChip';
import AssignmentTypeTreeNode from './AssignmentTypeTreeNode';

interface AssignmentTypeFilterProps {
  options: IAssignmentTypeFilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

const AssignmentTypeFilter = ({ options, selected, onChange }: AssignmentTypeFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectedLeafOptions = getSelectedLeafOptions(options, selected);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (e: MouseEvent) => {
      if ((e.target as Element).closest?.('[data-radix-popper-content-wrapper]')) return;
      if (containerRef.current?.contains(e.target as Node)) return;

      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen]);

  const removeValue = (value: string) => onChange(selected.filter(v => v !== value));

  const toggleIsOpen = () => setIsOpen(!isOpen);

  return (
    <div className="tw-w-[94%]">
      <span className="tw-font-bold tw-block tw-mb-1 tw-ml-1" id="assignment-type-label">
        {I18n.t('assignment_type')}
      </span>
      <div ref={containerRef} className="tw-relative">
        {/* AIDEV-NOTE: not a <button> — it contains each chip's own remove <button>, and nested
            buttons are invalid HTML (and would let a chip removal bubble up into toggling the field). */}
        <div
          aria-expanded={isOpen}
          aria-labelledby="assignment-type-label"
          className={`tw-w-full tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-1
                     tw-min-h-[38px] tw-px-2 tw-py-1 tw-border tw-border-solid tw-border-light-grey
                     tw-rounded tw-bg-white tw-cursor-pointer`}
          role="button"
          tabIndex={0}
          onClick={toggleIsOpen}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' && e.key !== ' ') return;

            e.preventDefault();
            toggleIsOpen();
          }}
        >
          <div className="tw-flex tw-flex-wrap tw-gap-1 tw-flex-1">
            {selectedLeafOptions.length === 0 && (
              <span className="tw-text-med-dark-grey">{I18n.t('select_an_option')}</span>
            )}
            {selectedLeafOptions.map(option => (
              <AssignmentTypeChip
                key={option.value}
                label={option.label}
                onRemove={() => removeValue(option.value)}
              />
            ))}
          </div>
          <Icon icon="ChevronDown" size={12} />
        </div>

        {isOpen && (
          <div
            className={`tw-absolute tw-left-0 tw-right-0 tw-top-full tw-mt-1 tw-z-10 tw-bg-white
                       tw-border tw-border-solid tw-border-light-grey tw-rounded tw-shadow-lg
                       tw-max-h-[300px] tw-overflow-y-auto tw-px-3 tw-py-2`}
          >
            {options.map(option => (
              <AssignmentTypeTreeNode
                key={option.value}
                option={option}
                selected={selected}
                onToggle={toggled => onChange(toggleOption(toggled, selected))}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignmentTypeFilter;
